import * as React from "react"

import type {CtaCountFeedback, CtaInteractionEffect} from "./ctaShared"

export const CTA_INTERACTION_CONFIRM_DURATION_MS = 240
export const CTA_COUNT_PULSE_DURATION_MS = 220
const CTA_INTERACTION_STALE_WINDOW_MS = 1200

type FeedbackPhase = "idle" | "confirm"

type Args = {
    interactionEffect: CtaInteractionEffect
    countFeedback: CtaCountFeedback
    active: boolean
    count?: number | null
}

function normalizeCount(count?: number | null) {
    return typeof count === "number" && Number.isFinite(count) ? count : null
}

export function useCtaInteractionFeedback({
    interactionEffect,
    countFeedback,
    active,
    count,
}: Args) {
    const [pressed, setPressed] = React.useState(false)
    const [feedbackPhase, setFeedbackPhase] = React.useState<FeedbackPhase>("idle")
    const [countChanged, setCountChanged] = React.useState(false)

    const mountedRef = React.useRef(false)
    const interactionArmedRef = React.useRef(false)
    const confirmTimeoutRef = React.useRef<number | null>(null)
    const countTimeoutRef = React.useRef<number | null>(null)
    const staleInteractionTimeoutRef = React.useRef<number | null>(null)
    const previousActiveRef = React.useRef(Boolean(active))
    const previousCountRef = React.useRef<number | null>(normalizeCount(count))

    const clearTimeoutIfNeeded = React.useCallback((timeoutRef: React.MutableRefObject<number | null>) => {
        if (timeoutRef.current != null) {
            window.clearTimeout(timeoutRef.current)
            timeoutRef.current = null
        }
    }, [])

    const clearAllTimers = React.useCallback(() => {
        clearTimeoutIfNeeded(confirmTimeoutRef)
        clearTimeoutIfNeeded(countTimeoutRef)
        clearTimeoutIfNeeded(staleInteractionTimeoutRef)
    }, [clearTimeoutIfNeeded])

    React.useEffect(() => clearAllTimers, [clearAllTimers])

    const armInteraction = React.useCallback(() => {
        if (interactionEffect === "none") {
            return
        }

        interactionArmedRef.current = true
        clearTimeoutIfNeeded(staleInteractionTimeoutRef)
        staleInteractionTimeoutRef.current = window.setTimeout(() => {
            interactionArmedRef.current = false
            staleInteractionTimeoutRef.current = null
        }, CTA_INTERACTION_STALE_WINDOW_MS)
    }, [clearTimeoutIfNeeded, interactionEffect])

    React.useEffect(() => {
        const nextActive = Boolean(active)
        const nextCount = normalizeCount(count)

        if (!mountedRef.current) {
            mountedRef.current = true
            previousActiveRef.current = nextActive
            previousCountRef.current = nextCount
            return
        }

        const activeChanged = previousActiveRef.current !== nextActive
        const countChangedNow = previousCountRef.current !== nextCount

        previousActiveRef.current = nextActive
        previousCountRef.current = nextCount

        if (!interactionArmedRef.current) {
            return
        }

        if (!activeChanged && !countChangedNow) {
            return
        }

        interactionArmedRef.current = false
        clearTimeoutIfNeeded(staleInteractionTimeoutRef)

        if (interactionEffect !== "none") {
            clearTimeoutIfNeeded(confirmTimeoutRef)
            setFeedbackPhase("confirm")
            confirmTimeoutRef.current = window.setTimeout(() => {
                setFeedbackPhase("idle")
                confirmTimeoutRef.current = null
            }, CTA_INTERACTION_CONFIRM_DURATION_MS)
        }

        if (countFeedback === "pulse" && countChangedNow && nextCount != null) {
            clearTimeoutIfNeeded(countTimeoutRef)
            setCountChanged(true)
            countTimeoutRef.current = window.setTimeout(() => {
                setCountChanged(false)
                countTimeoutRef.current = null
            }, CTA_COUNT_PULSE_DURATION_MS)
        }
    }, [active, clearTimeoutIfNeeded, count, countFeedback, interactionEffect])

    const handlePressStart = React.useCallback(() => {
        if (interactionEffect === "none") {
            return
        }
        setPressed(true)
    }, [interactionEffect])

    const handlePressEnd = React.useCallback(() => {
        setPressed(false)
    }, [])

    // QA-21: a click blurs only when it completes a POINTER sequence (latched on pointerdown, cleared
    // on keydown), so keyboard Enter/Space keeps focus. Never read `pointerType`/`detail` off the click:
    // the two-event contract, AGENTS.md rule 26 / AGENTS/frontend-runtime.md.
    const pointerPressRef = React.useRef(false)

    const handlePointerDownCapture = React.useCallback(() => {
        pointerPressRef.current = true
        handlePressStart()
    }, [handlePressStart])

    const blurAfterPointerPress = React.useCallback((element: HTMLElement | null) => {
        if (pointerPressRef.current) {
            pointerPressRef.current = false
            element?.blur()
        }
    }, [])

    const handleKeyDownCapture = React.useCallback((event: React.KeyboardEvent<HTMLElement>) => {
        pointerPressRef.current = false
        if (event.key === " " || event.key === "Enter") {
            handlePressStart()
        }
    }, [handlePressStart])

    const rootInteractionProps = {
        "data-interaction-effect": interactionEffect,
        "data-feedback-phase": feedbackPhase,
        "data-count-feedback": countFeedback,
        "data-count-changed": countChanged ? "true" : "false",
        "data-pressed": pressed ? "true" : "false",
        onPointerDownCapture: handlePointerDownCapture,
        onPointerUpCapture: handlePressEnd,
        onPointerCancelCapture: handlePressEnd,
        onPointerOutCapture: handlePressEnd,
        onKeyDownCapture: handleKeyDownCapture,
        onKeyUpCapture: handlePressEnd,
        onBlurCapture: handlePressEnd,
    } as const

    return {
        rootInteractionProps,
        armInteraction,
        blurAfterPointerPress,
    }
}
